import { Dropdown } from '@glu/dropdown-react';
import { ComponentProps, useCallback, useEffect, useMemo, useState } from 'react';
import locale from '@glu/locale';
import { IFilterProps, IFilterable, IDropdownFilter } from '../Filters/Filters';

type TDropdownProps = ComponentProps<typeof Dropdown>;
type TOptionType = TDropdownProps['options'];
type TDropdownOnChange = NonNullable<TDropdownProps['onChange']>;

export interface IDropdownFilterFilterable extends IFilterable {
  /** The list of options available in the dropdown */
  filterOptions: IFilterable['filterOptions'] &
    TDropdownProps & {
      getOptions(filterable: IDropdownFilterFilterable): Promise<TOptionType>;
    };
}

export interface IDropdownFilterProps extends IFilterProps {
  /** The current value of the filter. Since state is not managed
   * locally this is required to display any current filter values.
   */
  value?: IDropdownFilter;
  /** Updated filterable element with options required for dropdown */
  filterable: IDropdownFilterFilterable;
}

export const defaultParseDropdownFilterValue = ({
  event,
  filterable,
}: {
  filterable: IFilterable;
  event: {
    name: string;
    selection: TOptionType;
    value?: boolean;
  };
}) => {
  const newValues = event.selection?.map((s) => s.id);
  const newLabels = event.selection?.map((s) => s.name);
  return { filterable, value: { values: newValues, labels: newLabels, type: 'set' } };
};

const DropdownFilter = ({ filterable, onChange, value }: IDropdownFilterProps) => {
  const [options, setOptions] = useState<TOptionType>([]);
  const { field, filterOptions, headerName } = filterable;
  const { values } = value || {};
  const dropdownValues = useMemo(() => values?.map((f) => ({ name: f, id: f })) || [], [values]);

  const handleChange = useCallback<TDropdownOnChange>(
    (name, selection, dropdownValue) => {
      const changeValue = (filterOptions.parseFilterValue || defaultParseDropdownFilterValue)({
        filterable,
        event: {
          name,
          selection,
          value: dropdownValue,
        },
      });
      onChange(changeValue);
    },
    [filterOptions.parseFilterValue, filterable, onChange]
  );

  useEffect(() => {
    /** TODO: need to handle error case. v13 also didn't handle an error case here */
    filterOptions.getOptions(filterable).then((resolvedOptions) => setOptions(resolvedOptions));
  }, [filterOptions, filterable]);

  return (
    <Dropdown
      disableInputAPI
      labelText={`${headerName} ${locale.get('dataComponents.filter')}`}
      name={field}
      onChange={handleChange}
      options={options}
      screenReaderLabel
      showSelected={1}
      showTooltip
      value={dropdownValues}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...filterOptions.componentProps}
    />
  );
};

export default DropdownFilter;
